import React, { useState } from 'react';
import { putDataAPI, postDataAPI } from '../../../utils/fetchData';

const FlightDetailModal = ({ flight, show, onClose, onRefresh }) => {
  const [status, setStatus] = useState(flight.status || 'option');
  const [updating, setUpdating] = useState(false);
  const [tracking, setTracking] = useState(false);
  const formatTime = (timeString) => {
    if (!timeString) return '';
    try {
      const date = new Date(timeString);
      if (isNaN(date.getTime())) return '';
      return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    } catch {
      return '';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch {
      return '';
    }
  };

  const getAirlineIcon = (airlineName) => {
    // Return empty string instead of emojis
    return '';
  };

  // Helper functions to handle both old and new flight data structure
  const getAirlineName = () => {
    if (typeof flight.airline === 'string') return flight.airline;
    return flight.airline?.name || 'Unknown Airline';
  };

  const getDepartureAirport = () => {
    return flight.departure?.airport || flight.origin || 'XXX';
  };

  const getArrivalAirport = () => {
    return flight.arrival?.airport || flight.destination || 'XXX';
  };

  const getDepartureCity = () => {
    return flight.departure?.city || flight.origin || 'Unknown';
  };

  const getArrivalCity = () => {
    return flight.arrival?.city || flight.destination || 'Unknown';
  };

  const getDepartureTime = () => {
    return flight.departure?.time || flight.departure_time;
  };

  const getArrivalTime = () => {
    return flight.arrival?.time || flight.arrival_time;
  };

  const getDepartureTerminal = () => {
    return flight.departure?.terminal || flight.departure_terminal;
  };

  const getDepartureGate = () => {
    return flight.departure?.gate || flight.departure_gate;
  };

  const getArrivalTerminal = () => {
    return flight.arrival?.terminal || flight.arrival_terminal;
  };

  const getArrivalGate = () => {
    return flight.arrival?.gate || flight.arrival_gate;
  };

  const formatDuration = () => {
    if (flight.duration && typeof flight.duration === 'number') {
      const hours = Math.floor(flight.duration / 60);
      const minutes = flight.duration % 60;
      return `${hours}h ${minutes}m`;
    }
    return flight.flight_duration || 'N/A';
  };

  const airlineName = getAirlineName();
  const departureAirport = getDepartureAirport();
  const arrivalAirport = getArrivalAirport();
  const departureCity = getDepartureCity();
  const arrivalCity = getArrivalCity();
  const departureTime = getDepartureTime();
  const arrivalTime = getArrivalTime();
  const departureTerminal = getDepartureTerminal();
  const departureGate = getDepartureGate();
  const arrivalTerminal = getArrivalTerminal();
  const arrivalGate = getArrivalGate();
  const duration = formatDuration();

  const handleStatusChange = async (newStatus) => {
    try {
      setUpdating(true);
      const token = JSON.parse(localStorage.getItem('dizeeToken'));
      
      const response = await putDataAPI(`user/tm/flights/${flight._id}`, {
        status: newStatus
      }, token);
      
      if (response.data.status === 200) {
        setStatus(newStatus);
        if (onRefresh) {
          onRefresh(); // Refresh the parent component to show updated status
        }
      }
    } catch (error) {
      console.error('Error updating flight status:', error);
      alert('Failed to update flight status. Please try again.');
    } finally {
      setUpdating(false);
    }
  };

  const getStatusColor = (statusValue) => {
    switch (statusValue) {
      case 'option': return 'rgba(255,255,255,0.3)';
      case 'selected': return '#FF9500';
      case 'booked': return '#34C759';
      case 'cancelled': return '#FF3B30';
      default: return 'rgba(255,255,255,0.3)';
    }
  };

  const getStatusLabel = (statusValue) => {
    switch (statusValue) {
      case 'option': return 'Option';
      case 'selected': return 'Selected';
      case 'booked': return 'Booked';
      case 'cancelled': return 'Cancelled';
      default: return 'Unknown';
    }
  };

  const handleTrackFlight = async () => {
    try {
      setTracking(true);
      const token = JSON.parse(localStorage.getItem('dizeeToken'));
      
      const response = await postDataAPI(`user/tm/flights/${flight._id}/track`, {}, token);
      
      if (response.data.status === 200) {
        alert('Flight tracking updated! Live status available.');
        if (onRefresh) {
          onRefresh();
        }
      }
    } catch (error) {
      console.error('Error tracking flight:', error);
      alert('Failed to track flight. Please try again.');
    } finally {
      setTracking(false);
    }
  };

  const getLiveStatusBadge = () => {
    if (!flight.tracking?.liveStatus) return null;
    
    const badges = {
      'scheduled': { label: 'SCHEDULED', color: 'rgba(255,255,255,0.8)', bg: 'rgba(255,255,255,0.15)' },
      'active': { label: 'IN FLIGHT', color: '#FF9500', bg: 'rgba(255,149,0,0.2)' },
      'completed': { label: 'LANDED', color: '#34C759', bg: 'rgba(52,199,89,0.2)' },
      'cancelled': { label: 'CANCELLED', color: '#FF3B30', bg: 'rgba(255,59,48,0.2)' },
      'diverted': { label: 'DIVERTED', color: '#FFD60A', bg: 'rgba(255,214,10,0.2)' }
    };
    
    return badges[flight.tracking.liveStatus] || badges['scheduled'];
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50" onClick={onClose}>
      <div 
        className="w-full max-w-[400px] h-[600px] mx-4 transform overflow-hidden rounded-lg bg-black border border-[rgba(255,255,255,0.1)] text-left align-middle shadow-xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 border-b border-[rgba(255,255,255,0.1)] flex-shrink-0">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-white text-[14px] font-medium">
              {departureAirport} — {arrivalAirport}
            </h2>
            <button onClick={onClose} className="text-white text-[12px] font-normal hover:opacity-70">Close</button>
          </div>
          <p className="text-[rgba(255,255,255,0.7)] text-[12px] leading-[1.4]">
            {formatDate(departureTime)} · {flight.stops || 0} stop{flight.stops !== 1 ? 's' : ''} · {flight.passengers || '1'} passenger
          </p>
        </div>

        {/* Flight Info - Scrollable Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-4">
          <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-[8px] p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-white text-[14px] font-medium">{formatTime(departureTime)}</p>
                <p className="text-[rgba(255,255,255,0.7)] text-[12px]">{departureAirport}</p>
              </div>
              <div className="flex-1 mx-4">
                <div className="h-[1px] bg-[rgba(255,255,255,0.2)] relative">
                  <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-1 h-1 bg-white rounded-full"></div>
                </div>
                <p className="text-[rgba(255,255,255,0.5)] text-[10px] text-center mt-1">
                  {duration}
                </p>
              </div>
              <div className="text-right">
                <p className="text-white text-[14px] font-medium">{formatTime(arrivalTime)}</p>
                <p className="text-[rgba(255,255,255,0.7)] text-[12px]">{arrivalAirport}</p>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-white text-[12px]">{airlineName}</span>
              {getLiveStatusBadge() && (
                <span 
                  className="px-2 py-1 rounded text-[9px] font-medium tracking-wider"
                  style={{
                    color: getLiveStatusBadge().color,
                    backgroundColor: getLiveStatusBadge().bg
                  }}
                >
                  {getLiveStatusBadge().label}
                </span>
              )}
            </div>
          </div>

          {/* Live Tracking Info */}
          {flight.tracking && (
            <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-[8px] p-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-white text-[12px] font-semibold">Live Tracking</p>
                <p className="text-[rgba(255,255,255,0.5)] text-[9px]">
                  Updated: {new Date(flight.tracking.lastUpdated).toLocaleTimeString()}
                </p>
              </div>
              
              {flight.tracking.delayMinutes && flight.tracking.delayMinutes !== 0 && (
                <div className="mb-2">
                  <span className={`text-[11px] font-semibold ${flight.tracking.delayMinutes > 0 ? 'text-[#FF9500]' : 'text-[#34C759]'}`}>
                    {flight.tracking.delayMinutes > 0 ? `Delayed ${flight.tracking.delayMinutes} min` : `Early by ${Math.abs(flight.tracking.delayMinutes)} min`}
                  </span>
                </div>
              )}

              {flight.tracking.actualDeparture?.gate && (
                <div className="text-[11px] text-[rgba(255,255,255,0.7)] mb-1">
                  Departure Gate: {flight.tracking.actualDeparture.gate}
                </div>
              )}

              {flight.tracking.actualArrival?.gate && (
                <div className="text-[11px] text-[rgba(255,255,255,0.7)]">
                  Arrival Gate: {flight.tracking.actualArrival.gate}
                </div>
              )}

              {flight.tracking.progress !== undefined && flight.tracking.liveStatus === 'active' && (
                <div className="mt-3">
                  <div className="flex justify-between text-[10px] text-[rgba(255,255,255,0.6)] mb-1">
                    <span>Flight Progress</span>
                    <span>{flight.tracking.progress}%</span>
                  </div>
                  <div className="w-full bg-[rgba(255,255,255,0.2)] rounded-full h-2">
                    <div 
                      className="bg-[#FF9500] h-2 rounded-full transition-all"
                      style={{ width: `${flight.tracking.progress}%` }}
                    ></div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Track Flight Button */}
          {(status === 'booked' || status === 'selected') && (
            <button
              onClick={handleTrackFlight}
              disabled={tracking}
              className="w-full bg-[rgba(255,149,0,0.2)] border border-[#FF9500] text-[#FF9500] rounded-[8px] px-3 py-2 text-[12px] font-medium transition-colors hover:bg-[rgba(255,149,0,0.3)] disabled:opacity-50"
            >
              {tracking ? 'Tracking...' : 'Update Live Status'}
            </button>
          )}

          {/* Details */}
          <div className="space-y-3">
            {departureTerminal && (
              <div className="flex justify-between text-[12px]">
                <span className="text-[rgba(255,255,255,0.6)]">Departure Terminal</span>
                <span className="text-white">{departureTerminal}</span>
              </div>
            )}
            {departureGate && (
              <div className="flex justify-between text-[12px]">
                <span className="text-[rgba(255,255,255,0.6)]">Gate</span>
                <span className="text-white">{departureGate}</span>
              </div>
            )}
            {arrivalTerminal && (
              <div className="flex justify-between text-[12px]">
                <span className="text-[rgba(255,255,255,0.6)]">Arrival Terminal</span>
                <span className="text-white">{arrivalTerminal}</span>
              </div>
            )}
            {arrivalGate && (
              <div className="flex justify-between text-[12px]">
                <span className="text-[rgba(255,255,255,0.6)]">Arrival Gate</span>
                <span className="text-white">{arrivalGate}</span>
              </div>
            )}
          </div>

          {/* Status Selector */}
          <div>
            <label className="block text-white text-[12px] font-medium mb-2">Booking Status</label>
            <div className="grid grid-cols-2 gap-2">
              {['option', 'selected', 'booked', 'cancelled'].map((statusOption) => (
                <button
                  key={statusOption}
                  onClick={() => !updating && handleStatusChange(statusOption)}
                  disabled={updating}
                  className={`px-3 py-2 rounded-[8px] text-[12px] font-medium transition-all border ${
                    status === statusOption
                      ? 'bg-[#FF9500] text-black border-[#FF9500]'
                      : 'bg-[rgba(255,255,255,0.05)] text-white border-[rgba(255,255,255,0.1)] hover:bg-[rgba(255,149,0,0.1)] hover:border-[rgba(255,149,0,0.3)]'
                  } ${updating ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {updating && status === statusOption ? 'Updating...' : getStatusLabel(statusOption)}
                </button>
              ))}
            </div>
          </div>

          {/* Passengers */}
          {flight.passengers_list && flight.passengers_list.length > 0 && (
            <div>
              <p className="text-white text-[12px] font-semibold mb-2">Passengers</p>
              <div className="flex -space-x-2">
                {flight.passengers_list.map((passenger, index) => (
                  <div
                    key={index}
                    className="w-8 h-8 rounded-full bg-[#FF9500] border-2 border-[#1C1C1E] flex items-center justify-center text-white text-[10px] font-semibold"
                  >
                    {passenger.name?.charAt(0) || '?'}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Action Button */}
          <button
            onClick={() => window.open(flight.booking_url || flight.booking_link || '#', '_blank')}
            className="w-full bg-[#FF9500] hover:bg-[#FF8800] text-black text-[12px] font-medium py-2 px-4 rounded-[8px] transition-all focus:outline-none"
          >
            Book on {airlineName}
          </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FlightDetailModal;

